/* eslint-disable no-plusplus */
/* eslint-disable no-param-reassign */
import { Mesh, ExtrudeGeometry, ShapeGeometry } from "three";
import { UVMapExtrudedShape, UVMapShape, } from "@rendering/vortex-core";
import { convertToShape, preserve, mirror } from "../common/pathHelper";
export var PathOperationType;
(function (PathOperationType) {
    PathOperationType[PathOperationType["moveTo"] = 0] = "moveTo";
    PathOperationType[PathOperationType["lineTo"] = 1] = "lineTo";
    PathOperationType[PathOperationType["quadraticCurveTo"] = 2] = "quadraticCurveTo";
    PathOperationType[PathOperationType["bezierCurveTo"] = 3] = "bezierCurveTo";
    PathOperationType[PathOperationType["arcTo"] = 4] = "arcTo";
})(PathOperationType || (PathOperationType = {}));
const MIN_CURVE_SEGMENTS = 8;
const MAX_CURVE_SEGMENTS = 35;
// TODO: Stop mutating params
// Centers a mesh according to its physical size
function centerMesh(mesh, physicalSize) {
    mesh.position.x = physicalSize.width * -0.5;
    mesh.position.y = physicalSize.height * -0.5;
    mesh.position.z = physicalSize.depth * -0.5;
}
// TODO: Extend logic to take curves/arcs into account
// Finds the maximum size of the path by scanning all path operations
function findMaxSize(pathOperations) {
    let maxX = 0;
    let maxY = 0;
    for (let i = 0; i < pathOperations.length; i++) {
        if (pathOperations[i].x > maxX) {
            maxX = pathOperations[i].x;
        }
        if (pathOperations[i].y > maxY) {
            maxY = pathOperations[i].y;
        }
    }
    return { width: maxX, height: maxY };
}
// Determines the curve count of the custom path based on its complexity
function getCurveSegmentCount(customPath) {
    let complexOperationCount = 0;
    // Sum up all the complex path operations in the primary path
    customPath.primary.forEach((operation) => {
        if (operation.type === PathOperationType.quadraticCurveTo ||
            operation.type === PathOperationType.bezierCurveTo ||
            operation.type === PathOperationType.arcTo) {
            complexOperationCount++;
        }
    });
    // Add all cutouts if they exist
    if (customPath.cutouts && customPath.cutouts.length > 0) {
        customPath.cutouts.forEach((cutout) => {
            cutout.forEach((operation) => {
                if (operation.type === PathOperationType.quadraticCurveTo ||
                    operation.type === PathOperationType.bezierCurveTo ||
                    operation.type === PathOperationType.arcTo) {
                    complexOperationCount++;
                }
            });
        });
    }
    // If there is nothing complex return the default
    if (complexOperationCount === 0) {
        return MAX_CURVE_SEGMENTS;
    }
    // Otherwise limit the curve count to be between the min and max and then a function of the operation count
    return Math.max(MIN_CURVE_SEGMENTS, Math.min(MAX_CURVE_SEGMENTS, Math.round(180 / complexOperationCount)));
}
export class CustomPathGeometry {
    // eslint-disable-next-line class-methods-use-this
    get key() {
        return "custom path";
    }
    constructor(customPath, trim) {
        Object.defineProperty(this, "maxSize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "customPath", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "curveSegments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        if (trim) {
            this.maxSize = {
                width: trim.bottomRightX - trim.topLeftX,
                height: trim.bottomRightY - trim.topLeftY,
            };
        }
        else {
            this.maxSize = findMaxSize(customPath.primary);
        }
        this.customPath = customPath;
        this.curveSegments = getCurveSegmentCount(customPath);
    }
    buildFront(physicalSize) {
        const frontShape = convertToShape(this.customPath, this.maxSize, { x: preserve, y: mirror }, { x: preserve, y: mirror });
        const geometry = new ShapeGeometry(frontShape, this.curveSegments);
        UVMapShape(geometry, physicalSize.width, physicalSize.height);
        const mesh = new Mesh(geometry);
        centerMesh(mesh, physicalSize);
        return mesh;
    }
    buildBack(physicalSize) {
        const backShape = convertToShape(this.customPath, this.maxSize, { x: mirror, y: mirror }, { x: mirror, y: mirror });
        const geometry = new ShapeGeometry(backShape, this.curveSegments);
        UVMapShape(geometry, physicalSize.width, physicalSize.height);
        const mesh = new Mesh(geometry);
        centerMesh(mesh, physicalSize);
        // Account for the reverse centering
        mesh.position.x += physicalSize.width;
        return mesh;
    }
    buildBody(physicalSize) {
        const extrudeSettings = {
            bevelEnabled: false,
            depth: physicalSize.depth,
            curveSegments: this.curveSegments,
        };
        const bodyShape = convertToShape(this.customPath, this.maxSize, { x: preserve, y: mirror }, { x: preserve, y: mirror });
        const mesh = new Mesh(new ExtrudeGeometry(bodyShape, extrudeSettings));
        UVMapExtrudedShape(mesh.geometry, physicalSize);
        centerMesh(mesh, physicalSize);
        return mesh;
    }
}
//# sourceMappingURL=customPathGeometry.js.map